import { refreshCartCount, refreshMiniCart, refreshUpsells } from './utils.js';

(() => {
  const OVERLAY_ID = 'upsell-popup-overlay';
  const USER_FLAG_TTL_MS = 2500;

  const getOverlay = () => document.getElementById(OVERLAY_ID);
  const open = () => window.__UPSELL_POPUP__ && window.__UPSELL_POPUP__.open();
  const close = () => window.__UPSELL_POPUP__ && window.__UPSELL_POPUP__.close();

  const isEnabled = () => {
    const el = getOverlay();
    return !!el && !el.hasAttribute('data-disabled');
  };

  const fetchCartCount = async () => {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return 0;
      const json = await res.json();
      return json && json.item_count ? json.item_count : 0;
    } catch {
      return 0;
    }
  };

  const installButtons = () => {
    const overlay = getOverlay();
    if (!overlay) return;
    overlay.addEventListener('click', async (e) => {
      if (e.target === overlay) return close();

      const closeBtn = e.target.closest('.upsell-popup__close');
      if (closeBtn) return close();

      const dismissBtn = e.target.closest('.upsell-popup__dismiss');
      if (dismissBtn) return close();

      const addBtn = e.target.closest('.upsell-popup__add');
      if (addBtn) {
        const variantId = addBtn.getAttribute('data-variant-id');
        if (!variantId) return close();
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          });
        } catch {}
        close();
        try {
          refreshCartCount();
          refreshMiniCart();
          setTimeout(() => {
            refreshUpsells();
          }, 1000);
        } catch {}
      }
    });
  };

  let lastUserInitiatedAddAt = 0;
  const markUserInitiatedAdd = () => {
    lastUserInitiatedAddAt = Date.now();
  };
  const isRecentUserInitiatedAdd = () => Date.now() - lastUserInitiatedAddAt <= USER_FLAG_TTL_MS;

  const watchUserAddInteractions = () => {
    document.addEventListener(
      'click',
      (e) => {
        const t = e.target.closest(
          "button[name='add'], input[type='submit'][name='add'], .product-form__submit, .c-grouped-products__add-to-cart, #c-buy-together__add-to-cart, form[action*='/cart/add'] button, form[data-type='add-to-cart-form'] button",
        );
        if (t) markUserInitiatedAdd();
      },
      true,
    );

    document.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        const action = form.getAttribute('action') || '';
        if (action.includes('/cart/add')) markUserInitiatedAdd();
      },
      true,
    );
  };

  const wrapFetch = () => {
    const original = window.fetch;
    if (!original || original.__upsellWrapped) return;

    const getUpsellVariantIdSet = () => {
      const el = getOverlay();
      if (!el) return new Set();
      const raw = el.getAttribute('data-upsell-variant-ids') || '';
      return new Set(
        raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => Number(s))
          .filter((n) => !Number.isNaN(n)),
      );
    };

    const parseAddedVariantIds = (init) => {
      try {
        if (!init) return [];
        const body = init.body;
        if (!body) return [];
        if (typeof body === 'string') {
          try {
            const json = JSON.parse(body);
            if (Array.isArray(json?.items)) return json.items.map((i) => Number(i.id)).filter((n) => !Number.isNaN(n));
            if (json?.id) return [Number(json.id)].filter((n) => !Number.isNaN(n));
          } catch {
            const params = new URLSearchParams(body);
            const id = params.get('id');
            if (id) return [Number(id)].filter((n) => !Number.isNaN(n));
          }
        } else if (body instanceof FormData) {
          const id = body.get('id');
          if (id) return [Number(id)].filter((n) => !Number.isNaN(n));
        } else if (body instanceof URLSearchParams) {
          const id = body.get('id');
          if (id) return [Number(id)].filter((n) => !Number.isNaN(n));
        }
      } catch {}
      return [];
    };

    const dryCheckCache = new Map();

    const resolveIsDryByVariantId = async (variantId) => {
      try {
        const isChesapeake = (document.body && document.body.getAttribute('data-theme-variant')) === 'chesapeake';
        const storefrontToken = isChesapeake ? '48a8c8634314973cc3219af2dd69c47f' : '92319d6e9368255090ebb64b4af840ac';
        const storefrontUrl = '/api/2025-04/graphql.json';
        const dryMetafieldValues = ['Dry'];

        const gid = `gid://shopify/ProductVariant/${variantId}`;
        const query = `query($id: ID!) {\n          nodes(ids: [$id]) {\n            ... on ProductVariant {\n              id\n              product { id title handle metafield(namespace: \"magento\", key: \"is_dry\") { value } }\n            }\n          }\n        }`;
        const resp = await original(storefrontUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Shopify-Storefront-Access-Token': storefrontToken,
          },
          body: JSON.stringify({ query, variables: { id: gid } }),
          credentials: 'same-origin',
        });
        if (!resp.ok) {
          return false;
        }
        const payload = await resp.json();
        const node = payload?.data?.nodes?.[0];

        const productGid = node?.product?.id || '';
        const metafieldText = (node?.product?.metafield?.value || '').trim();
        const productNumericId = Number(productGid.split('/').pop());
        const isDryProduct = dryMetafieldValues.includes(metafieldText);
        if (productNumericId) dryCheckCache.set(productNumericId, isDryProduct);

        return isDryProduct;
      } catch (error) {
        return false;
      }
    };

    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isAdd = url.includes('/cart/add');
      let beforeCountPromise = null;
      if (isAdd) beforeCountPromise = fetchCartCount();
      const p = original(input, init);
      if (isAdd) {
        p.then(async (res) => {
          if (!res.ok) return;
          try {
            const addedIds = parseAddedVariantIds(init || {});

            const overlay = getOverlay();
            const standardIds = getUpsellVariantIdSet();
            const dryIdsRaw = (overlay?.getAttribute('data-upsell-dry-variant-ids') || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
              .map((s) => Number(s))
              .filter((n) => !Number.isNaN(n));
            const dryIds = new Set(dryIdsRaw);

            let isDryContext = false;
            if (addedIds.length > 0) {
              isDryContext = await resolveIsDryByVariantId(addedIds[0]);
            }

            const hasDryUpsellConfigured = dryIds.size > 0;

            const chosenIds = isDryContext && dryIds.size ? dryIds : standardIds;
            const isSameProduct = addedIds.some((id) => chosenIds.has(Number(id)));

            const before = await beforeCountPromise;

            let after = null;
            try {
              const resJson = await res.clone().json();
              if (typeof resJson?.item_count === 'number') after = resJson.item_count;
            } catch {}
            if (after === null) {
              after = await fetchCartCount();
            }
            if (
              isEnabled() &&
              !isSameProduct &&
              before === 0 &&
              Number(after) > 0 &&
              isRecentUserInitiatedAdd() &&
              document.visibilityState === 'visible' &&
              !isDryContext
            ) {
              if (overlay) {
                overlay.setAttribute('data-upsell-mode', isDryContext ? 'dry' : 'standard');
                const dryEl = overlay.querySelector('.upsell-mode--dry');
                const stdEls = overlay.querySelectorAll('.upsell-mode--standard');
                if (dryEl) dryEl.style.display = isDryContext ? '' : 'none';
                stdEls.forEach((el) => {
                  el.style.display = isDryContext ? 'none' : '';
                });
              }
              open();
            }
          } catch {}
        }).catch(() => {});
      }
      return p;
    };
    window.fetch.__upsellWrapped = true;
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (!getOverlay()) return;
    installButtons();
    watchUserAddInteractions();
    wrapFetch();
  });
})();
